import React, { useState } from "react";
import { Modal, DatePicker, Switch } from "antd";
import { SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { requestDocument } from "../../../../core/data/redux/recruitment/thunks";
import { notify } from "../../../../services/toast.config";

const DOCUMENT_CONFIGS = [
  {
    type: "resume",
    label: "Resume / CV",
    description: "Updated curriculum vitae or resume",
    icon: "ti-file-type-pdf",
    color: "#f43f5e",
    bgColor: "#fff1f2",
    borderColor: "#fecdd3",
  },
  {
    type: "id_proof",
    label: "Government ID Proof",
    description: "Passport, Aadhaar, PAN, or Driver's License",
    icon: "ti-id",
    color: "#3b82f6",
    bgColor: "#eff6ff",
    borderColor: "#bfdbfe",
  },
  {
    type: "address_proof",
    label: "Address Proof",
    description: "Utility bill, bank statement, or rental agreement",
    icon: "ti-home",
    color: "#8b5cf6",
    bgColor: "#f5f3ff",
    borderColor: "#ddd6fe",
  },
  {
    type: "education_certificate",
    label: "Education Certificate",
    description: "Degree certificates and mark sheets",
    icon: "ti-certificate",
    color: "#f59e0b",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
  },
  {
    type: "experience_letter",
    label: "Experience Letter",
    description: "Relieving or experience letters from previous employers",
    icon: "ti-briefcase",
    color: "#10b981",
    bgColor: "#ecfdf5",
    borderColor: "#a7f3d0",
  },
  {
    type: "salary_slip",
    label: "Salary Slips",
    description: "Last 3 months salary slips",
    icon: "ti-receipt",
    color: "#06b6d4",
    bgColor: "#ecfeff",
    borderColor: "#a5f3fc",
  },
  {
    type: "reference_letter",
    label: "Reference Letter",
    description: "Professional reference from previous managers",
    icon: "ti-user-check",
    color: "#6366f1",
    bgColor: "#eef2ff",
    borderColor: "#c7d2fe",
  },
  {
    type: "portfolio",
    label: "Portfolio",
    description: "Work samples, case studies, or project links",
    icon: "ti-folder",
    color: "#ec4899",
    bgColor: "#fdf2f8",
    borderColor: "#fbcfe8",
  },
  {
    type: "other",
    label: "Other Document",
    description: "Any other document required for this role",
    icon: "ti-file-text",
    color: "#64748b",
    bgColor: "#f8fafc",
    borderColor: "#e2e8f0",
  },
] as const;

interface SelectedDoc {
  type: string;
  is_mandatory: boolean;
  deadline: string | null;
}

interface DocumentRequestModalProps {
  show: boolean;
  onClose: () => void;
  applicationId: number;
  candidateName: string;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "#64748b",
  fontWeight: 700,
};

const DocumentRequestModal: React.FC<DocumentRequestModalProps> = ({
  show,
  onClose,
  applicationId,
  candidateName,
}) => {
  const [selectedDocs, setSelectedDocs] = useState<SelectedDoc[]>([]);
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const isSelected = (type: string) =>
    selectedDocs.some((d) => d.type === type);

  const toggleDoc = (type: string) => {
    setSelectedDocs((prev) =>
      prev.some((d) => d.type === type)
        ? prev.filter((d) => d.type !== type)
        : [...prev, { type, is_mandatory: true, deadline: null }],
    );
  };

  const updateDoc = (type: string, patch: Partial<SelectedDoc>) => {
    setSelectedDocs((prev) =>
      prev.map((d) => (d.type === type ? { ...d, ...patch } : d)),
    );
  };

  const handleClose = () => {
    setSelectedDocs([]);
    setCustomMessage("");
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedDocs.length === 0) {
      notify.warn("Please select at least one document type.");
      return;
    }
    setSending(true);
    try {
      await dispatch(
        requestDocument({
          applicationId,
          documentDetails: selectedDocs,
          messageText: customMessage || undefined,
        }),
      ).unwrap();
      notify.success(
        `${selectedDocs.length} document request${selectedDocs.length > 1 ? "s" : ""} sent successfully`,
      );
      handleClose();
    } catch (err: any) {
      notify.error(
        err ||
          err?.data?.error?.message ||
          err?.data?.message ||
          err ||
          "Failed to send document request",
      );
    } finally {
      setSending(false);
    }
  };

  const selectedCount = selectedDocs.length;

  return (
    <Modal
      open={show}
      onCancel={handleClose}
      width={680}
      zIndex={1050}
      getContainer={() => document.body}
      style={{ top: "4vh", padding: 0 }}
      styles={{
        content: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
          borderRadius: 14,
        },
        header: {
          flexShrink: 0,
          padding: "18px 24px 16px",
          borderBottom: "1px solid #f1f5f9",
          marginBottom: 0,
        },
        body: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: 0,
          minHeight: 0,
        },
        footer: {
          flexShrink: 0,
          padding: "14px 24px",
          borderTop: "1px solid #f1f5f9",
          marginTop: 0,
        },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.5)",
        },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 12px rgba(99,102,241,0.25)",
            }}>
            <i
              className="ti ti-file-upload"
              style={{ fontSize: 17, color: "#fff" }}
            />
          </div>
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: "#0f172a",
                lineHeight: 1.3,
              }}>
              Request Documents
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 400,
                marginTop: 1,
              }}>
              From&nbsp;
              <span style={{ color: "#6366f1", fontWeight: 600 }}>
                {candidateName}
              </span>
            </div>
          </div>
          {selectedCount > 0 && (
            <div
              style={{
                marginLeft: "auto",
                marginRight: 36,
                background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                color: "#fff",
                borderRadius: 20,
                padding: "2px 12px",
                fontSize: 12,
                fontWeight: 700,
              }}>
              {selectedCount} selected
            </div>
          )}
        </div>
      }
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered={false}
      footer={[
        <div
          key="footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 4px",
          }}>
          <button
            className="btn btn-light border"
            style={{
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 13,
              padding: "7px 20px",
            }}
            onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn"
            style={{
              borderRadius: 8,
              background:
                selectedCount === 0
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
              color: selectedCount === 0 ? "#94a3b8" : "#fff",
              fontWeight: 600,
              fontSize: 13,
              padding: "7px 24px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow:
                selectedCount > 0 ? "0 4px 14px rgba(99,102,241,0.3)" : "none",
              transition: "all 0.2s",
              border: "none",
            }}
            onClick={handleSubmit}
            disabled={sending || selectedCount === 0}>
            {sending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: 14, height: 14, borderWidth: 2 }}
                />
                Sending...
              </>
            ) : (
              <>
                <SendOutlined style={{ fontSize: 13 }} />
                Send Request
                {selectedCount > 0 && (
                  <span
                    style={{
                      background: "rgba(255,255,255,0.25)",
                      borderRadius: 10,
                      padding: "1px 8px",
                      fontSize: 11,
                    }}>
                    {selectedCount}
                  </span>
                )}
              </>
            )}
          </button>
        </div>,
      ]}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px 4px" }}>
        <div
          style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)",
            border: "1px solid #bfdbfe",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 20,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}>
          <i
            className="ti ti-info-circle"
            style={{
              fontSize: 17,
              color: "#3b82f6",
              marginTop: 1,
              flexShrink: 0,
            }}
          />
          <div style={{ fontSize: 12.5, color: "#1e40af", lineHeight: 1.5 }}>
            The candidate will receive a <strong>secure link via email</strong>{" "}
            to upload the requested documents directly to their profile.
          </div>
        </div>

        <div style={{ ...LABEL_STYLE, marginBottom: 12 }}>
          <i className="ti ti-files me-1" />
          Select Documents
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 20,
          }}>
          {DOCUMENT_CONFIGS.map((doc) => {
            const selected = isSelected(doc.type);
            const docData = selectedDocs.find((d) => d.type === doc.type);
            return (
              <div
                key={doc.type}
                style={{
                  border: `1.5px solid ${selected ? doc.color + "50" : "#e2e8f0"}`,
                  borderRadius: 12,
                  background: selected ? doc.bgColor : "#fafafa",
                  overflow: "hidden",
                  transition: "all 0.18s",
                  boxShadow: selected ? `0 2px 12px ${doc.color}18` : "none",
                }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "13px 16px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleDoc(doc.type)}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      border: `2px solid ${selected ? doc.color : "#cbd5e1"}`,
                      background: selected ? doc.color : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}>
                    {selected && (
                      <i
                        className="ti ti-check"
                        style={{ fontSize: 12, color: "#fff" }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: selected ? doc.color + "22" : "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}>
                    <i
                      className={`ti ${doc.icon}`}
                      style={{
                        fontSize: 18,
                        color: selected ? doc.color : "#94a3b8",
                        transition: "color 0.15s",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: selected ? 700 : 600,
                        fontSize: 13.5,
                        color: selected ? doc.color : "#0f172a",
                        transition: "color 0.15s",
                        lineHeight: 1.3,
                      }}>
                      {doc.label}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "#94a3b8",
                        marginTop: 2,
                      }}>
                      {doc.description}
                    </div>
                  </div>
                  {selected && (
                    <i
                      className="ti ti-chevron-down"
                      style={{ fontSize: 14, color: doc.color, flexShrink: 0 }}
                    />
                  )}
                </div>

                {selected && docData && (
                  <div
                    style={{
                      borderTop: `1px solid ${doc.borderColor}`,
                      padding: "12px 16px 14px",
                      background: "#fff",
                    }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div style={{ ...LABEL_STYLE, marginBottom: 6 }}>
                          Deadline
                        </div>
                        <DatePicker
                          size="middle"
                          style={{
                            width: "100%",
                            borderRadius: 8,
                            background: "#f8fafc",
                          }}
                          value={
                            docData.deadline ? dayjs(docData.deadline) : null
                          }
                          onChange={(_, dateStr) =>
                            updateDoc(doc.type, {
                              deadline:
                                (Array.isArray(dateStr)
                                  ? dateStr[0]
                                  : dateStr) || null,
                            })
                          }
                          disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
                          placeholder="Optional deadline"
                          allowClear
                        />
                      </div>
                      <div className="col-md-6">
                        <div style={{ ...LABEL_STYLE, marginBottom: 6 }}>
                          Mandatory
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            height: 32,
                          }}>
                          <Switch
                            size="small"
                            checked={docData.is_mandatory}
                            onChange={(v) =>
                              updateDoc(doc.type, { is_mandatory: v })
                            }
                            style={{
                              background: docData.is_mandatory
                                ? doc.color
                                : undefined,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 12,
                              color: docData.is_mandatory
                                ? doc.color
                                : "#94a3b8",
                              fontWeight: 500,
                            }}>
                            {docData.is_mandatory
                              ? "Required to proceed"
                              : "Optional"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ ...LABEL_STYLE, marginBottom: 8 }}>
            <i className="ti ti-message-circle me-1" />
            Personal Note{" "}
            <span
              style={{
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 400,
                color: "#94a3b8",
                fontSize: 11,
              }}>
              (optional — sent as description)
            </span>
          </div>
          <textarea
            className="form-control"
            rows={3}
            placeholder="E.g. Hi! We're excited to move forward with your application. Could you please share these documents at the earliest?"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            style={{
              borderRadius: 10,
              border: "1.5px solid #e2e8f0",
              background: "#f8fafc",
              fontSize: 13,
              resize: "vertical",
              padding: "10px 14px",
              color: "#0f172a",
              lineHeight: 1.6,
              outline: "none",
              boxShadow: "none",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        {selectedCount > 0 && (
          <div
            style={{
              marginBottom: 8,
              padding: "12px 16px",
              background: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)",
              borderRadius: 10,
              border: "1px solid #c7d2fe",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 6,
            }}>
            <span style={{ fontSize: 12, color: "#4338ca", fontWeight: 600 }}>
              Requesting:
            </span>
            {selectedDocs.map((d) => {
              const cfg = DOCUMENT_CONFIGS.find((c) => c.type === d.type)!;
              return (
                <span
                  key={d.type}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 10px",
                    borderRadius: 20,
                    fontSize: 11.5,
                    fontWeight: 600,
                    background: cfg.bgColor,
                    color: cfg.color,
                    border: `1px solid ${cfg.borderColor}`,
                  }}>
                  <i className={`ti ${cfg.icon}`} style={{ fontSize: 11 }} />
                  {cfg.label}
                  {d.is_mandatory && (
                    <span style={{ color: "#ef4444", fontSize: 10 }}>*</span>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocumentRequestModal;
